import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { concat, EMPTY, fromEvent, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { UserService } from 'src/app/modules/account/shared/user.service';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { setUserDetailsData } from 'src/app/store/user-details/user-details.actions';
import { normaliseVirtualsVerdict } from 'src/app/store/user-details/user-details.reducer';
import { selectVirtualsVerdict } from 'src/app/store/user-details/user-details.selectors';
import { IAccountVirtualsVerdict } from 'src/app/store/user-details/user-details.state';
import {
  isStoredVerdictUsable,
  isVerdictComputeError,
  verdictBelongsTo,
} from './virtuals-verdict';

/**
 * Backoff for a FAILED account request while someone is signed in: three
 * more tries, 2 s, 10 s, then 30 s after the failure before it. Without it a
 * failed boot fetch (a cold backend, a dropped connection) left the store
 * without this account's verdict — the VIRTUEL entry hidden — until the
 * player happened to hide and re-show the tab. Bounded on purpose: about 42 s
 * of trying, at most three extra calls per failure stretch, then the ordinary
 * triggers take over again. Reset by any answer that leaves the account with
 * a decision (an answer whose verdict is a compute ERROR, with no earlier
 * decision for the reducer to keep, counts as a failure).
 */
export const ACCOUNT_RETRY_DELAYS_MS: readonly number[] = [2000, 10000, 30000];

/** Options for storeIfCurrent. */
export interface StoreIfCurrentOptions {
  /**
   * Dispatch the payload only when its virtuals verdict differs from the one
   * stored for this account (visible / reason). For a caller that refetches
   * the account often and only for its balance — the lobby, on every
   * game.cycle.end — so an unchanged verdict does not re-fire every
   * selectUserDetailsData subscriber each round.
   */
  onlyIfVerdictChanged?: boolean;
}

// Keeps the server's virtuals verdict (GET /user/getUserAccount `virtuals`)
// current in a tab that stays open, and is the ONE place the account payload
// is fetched for the layout, the header and the /virtuals guard.
//
// Why it exists. The VIRTUEL entry is now decided from the account payload
// alone (ProvidersVisibilityService), and that payload is only fetched at
// boot, at login and on a few page actions. An ops change — a rollout list
// edit, a block, the global switch — would otherwise reach a tab only on its
// next reload. Two triggers cover the realistic gaps, and nothing polls:
//
//   (a) a LOGIN CHANGE: the credential moves to a uid the stored verdict does
//       not belong to. It first waits for any account request already in
//       flight (the boot fetch, the header's post-login refresh) and only
//       then looks again, so the common case costs no extra call; it asks
//       only when what landed is still someone else's (or nothing at all).
//   (b) the tab coming back to the FOREGROUND (visibilitychange → 'visible')
//       with a verdict older than VIRTUALS_VERDICT_TTL_MS — the shape of "left
//       the tab open over lunch".
// The lobby page's own balance-change refetch stores the payload too (through
// storeIfCurrent, the same ownership rule, and only when its verdict moved),
// so a verdict also refreshes during play. So do TndPageGuard's and
// StaticPageActionsService's own account fetches: every account payload
// reaching the store passes storeIfCurrent.
//
// SINGLE-FLIGHT. refreshAccount() shares one in-flight request between every
// caller that asks for the SAME credential, so the boot fetches of the header
// and the layout (two calls before this existed), a guard entry and trigger
// (a) collapse into one. A request started for another credential is never
// shared: its answer would be that other account's, and the TokenInterceptor
// stamps the token when the request leaves. Nothing is cached once the
// request settles — the store is the cache.
//
// BROWSER ONLY. start() does nothing on the server: there is no credential
// there, no document visibility, and a per-user verdict must never be fetched
// into an SSR render. A failed refresh keeps the stored verdict (a network
// blip must not hide the entry for the session, which is the bug this whole
// change removes) and, while signed in, is tried again on a short bounded
// backoff (ACCOUNT_RETRY_DELAYS_MS) — the case it matters for is a failed
// BOOT fetch, which leaves no verdict at all to keep. A logout cancels it.
// The server still refuses a launch it would not allow.
@Injectable({ providedIn: 'root' })
export class VirtualsVerdictRefreshService implements OnDestroy {
  private readonly isBrowser: boolean;
  private started = false;
  private readonly stop$ = new Subject<void>();
  /** The one shared account request, while it is in flight. */
  private inFlight$: Observable<any> | null = null;
  /** The credential uid inFlight$ was started for (null = signed out). */
  private inFlightUid: string | null = null;
  /** The pending backoff retry (scheduleRetry), if any. */
  private retryTimer: any = null;
  /** How many retries the current failure stretch has used. */
  private retryAttempts = 0;
  /** The account that stretch belongs to; another one starts from zero. */
  private retryUid: string | null = null;

  constructor(
    private authService: AuthService,
    private store: Store,
    private userService: UserService,
    private zone: NgZone,
    @Inject(DOCUMENT) private document: any,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * Arms both triggers. Idempotent: MainLayoutComponent calls it from every
   * ngOnInit, and the layout is recreated after /auth and /maintenance, but
   * the listeners live with this root service, once.
   */
  start(): void {
    if (!this.isBrowser || this.started || !this.document) {
      return;
    }
    this.started = true;

    // A logout ends any failure stretch: a retry scheduled for the account
    // that just left has nobody to fetch for.
    this.authService.authStatusSub
      .pipe(takeUntil(this.stop$))
      .subscribe((credential: any) => {
        if (!credential || !credential.uid) {
          this.resetRetry();
        }
      });

    this.authService.authStatusSub
      .pipe(
        map((credential: any) => (credential && credential.uid) || null),
        distinctUntilChanged(),
        filter((uid: string | null) => !!uid),
        // switchMap: a second login change while the first is still waiting
        // supersedes it.
        switchMap((uid: string) => this.settled().pipe(map(() => uid))),
        takeUntil(this.stop$)
      )
      .subscribe((uid: string) => {
        if (this.currentUid() !== uid) {
          return;
        }
        if (!verdictBelongsTo(this.storedVerdict(), uid)) {
          this.zone.run(() => this.refreshQuietly());
        }
      });

    // Outside the zone: a visibility flip that needs nothing must not cost a
    // change-detection pass. A refresh re-enters the zone so the dispatch
    // reaches the `| async` bindings.
    this.zone.runOutsideAngular(() => {
      fromEvent(this.document, 'visibilitychange')
        .pipe(takeUntil(this.stop$))
        .subscribe(() => this.onVisibilityChange());
    });
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
    this.started = false;
    this.resetRetry();
  }

  /**
   * Fetches GET /user/getUserAccount, dispatches it into the store, and
   * returns it — shared with any caller already waiting on a request for the
   * same credential. The dispatch happens once per response, inside the
   * shared request, so every caller sees the store already updated.
   *
   * The dispatch is CONDITIONAL: a response is stored only while it still
   * belongs to the account signed in when it lands (see
   * belongsToCurrentAccount). Callers receive it either way — the guard
   * decides from the payload against its own uid already
   * (isRefetchedVisibleFor).
   *
   * Errors reach every subscriber (the guard turns one into "home"); the
   * request is released either way, so the next caller dials again. A
   * failure while signed in also schedules the backoff retry (once per
   * response, inside the shared request, like the dispatch), and so does an
   * answer that leaves the signed-in account with only a compute ERROR;
   * any other answer resets it.
   */
  refreshAccount(): Observable<any> {
    const uid = this.currentUid();
    if (this.inFlight$ && this.inFlightUid === uid) {
      return this.inFlight$;
    }
    const request$: Observable<any> = this.userService.getUserAccount().pipe(
      tap((user: any) => {
        this.storeIfCurrent(user, uid);
        // An answer ends a failure stretch — unless what the store now holds
        // for the signed-in account is a compute ERROR, i.e. that account
        // still has no decision at all (the reducer keeps an earlier one in
        // place of an ERROR). That is the boot fetch failing in a different
        // shape, and it gets the same bounded backoff.
        const stored = this.storedVerdict();
        if (verdictBelongsTo(stored, this.currentUid()) && isVerdictComputeError(stored)) {
          this.scheduleRetry();
        } else {
          this.resetRetry();
        }
      }),
      tap({
        error: () => {
          this.release(request$);
          this.scheduleRetry();
        },
        complete: () => this.release(request$),
      }),
      // refCount:false — a caller that goes away (a destroyed component, a
      // guard superseded by another navigation) must not cancel the request
      // the others are waiting on.
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.inFlight$ = request$;
    this.inFlightUid = uid;
    return request$;
  }

  /**
   * Writes an account payload into the store only when belongsToCurrentAccount
   * allows it, and says whether it passed that gate. `startedUid` is
   * currentUid() taken BEFORE the request left.
   *
   * Public for every caller that fetches the account with a request of its
   * own — the lobby's balance refetch (which needs a request that leaves
   * after the bet was debited), TndPageGuard, StaticPageActionsService — so
   * that every account payload reaching the store passes the same ownership
   * rule. A caller that gets `false` must not write anything else from that
   * payload either (its balances are the other account's too).
   *
   * With `onlyIfVerdictChanged`, a payload that passes the gate is
   * dispatched only when its verdict differs from the stored one; `true` is
   * still returned when it did not need to be, because the answer to "is
   * this the signed-in account's payload?" is the same.
   */
  storeIfCurrent(
    user: any,
    startedUid: string | null,
    options: StoreIfCurrentOptions = {}
  ): boolean {
    if (!this.belongsToCurrentAccount(user, startedUid)) {
      return false;
    }
    if (options.onlyIfVerdictChanged && this.sameVerdictAsStored(user)) {
      return true;
    }
    this.store.dispatch(setUserDetailsData({ data: user }));
    return true;
  }

  /**
   * Does this payload carry the verdict the store already holds for the same
   * account? Compared on what a consumer reads — the owner, `visible` and
   * `reason` — and not on `checkedAt`, which the server re-stamps on every
   * answer and would make every payload "different". A stored verdict that is
   * no longer usable (past the TTL, or a decision the reducer kept with
   * `checkedAt: 0` after an ERROR) never counts as "the same": the payload is
   * stored, so the next game cycle re-stamps it instead of leaving it stale.
   */
  private sameVerdictAsStored(user: any): boolean {
    const stored = this.storedVerdict();
    if (!stored || !user || !verdictBelongsTo(stored, user.fbId)) {
      return false;
    }
    if (!isStoredVerdictUsable(stored, user.fbId, Date.now())) {
      return false;
    }
    const incoming = normaliseVirtualsVerdict(user.virtuals);
    return incoming.visible === stored.visible && incoming.reason === stored.reason;
  }

  /**
   * May a response to a request started for `startedUid` be written into the
   * store NOW? The store holds ONE account, the one signed in, and a request
   * takes long enough for that to change under it: account A logs out (and
   * B logs in) while A's request is still out. Its late answer would
   * overwrite whatever B's own request had already stored — B's balance,
   * username and virtuals verdict replaced by A's until the next refetch — so
   * it is dropped instead.
   *
   *   - Signed in when it lands: stored only when the payload IS that account
   *     (its fbId equals the credential's uid). The payload's own fbId is the
   *     authority here, not the uid the request started with: the boot
   *     request leaves before Firebase has restored the credential (started
   *     for nobody) and is still that account's answer.
   *   - Signed out when it lands: stored only when the request also started
   *     signed out — "no account on both sides", the boot shape before the
   *     credential restores, which the header has always stored. A request
   *     started for an account and answered after its logout is dropped: the
   *     logout has already cleared the store, and it must stay cleared.
   */
  private belongsToCurrentAccount(user: any, startedUid: string | null): boolean {
    const currentUid = this.currentUid();
    if (currentUid) {
      return !!user && user.fbId === currentUid;
    }
    return startedUid === null;
  }

  private onVisibilityChange(): void {
    if (this.document.visibilityState !== 'visible') {
      return;
    }
    const uid = this.currentUid();
    if (!uid || isStoredVerdictUsable(this.storedVerdict(), uid, Date.now())) {
      return;
    }
    this.zone.run(() => this.refreshQuietly());
  }

  /** A refresh nobody waits on: its only output is the store dispatch. */
  private refreshQuietly(): void {
    this.refreshAccount().subscribe({
      error: () => {
        // Deliberately ignored — see the class comment: the stored verdict
        // stands, and the next trigger asks again.
      },
    });
  }

  /**
   * Completes once the request in flight (if any) has settled, whatever its
   * outcome — the moment the store says what that request had to say.
   */
  private settled(): Observable<null> {
    const pending = this.inFlight$;
    if (!pending) {
      return of(null);
    }
    return concat(
      pending.pipe(
        ignoreElements(),
        catchError(() => EMPTY)
      ),
      of(null)
    );
  }

  /**
   * After a failed account request: try again on the next backoff step, if
   * someone is still signed in, nothing is already scheduled and the stretch
   * has steps left. The timer runs OUTSIDE the Angular zone — a pending 30 s
   * timeout inside it would hold the app "unstable" (and so delay anything
   * that waits for stability) — and the refresh re-enters the zone so its
   * dispatch reaches the `| async` bindings. A retry that fires after the
   * account changed or logged out does nothing.
   */
  private scheduleRetry(): void {
    const uid = this.currentUid();
    if (!this.isBrowser || !uid || this.retryTimer !== null) {
      return;
    }
    if (this.retryUid !== uid) {
      this.retryUid = uid;
      this.retryAttempts = 0;
    }
    if (this.retryAttempts >= ACCOUNT_RETRY_DELAYS_MS.length) {
      return;
    }
    const delay = ACCOUNT_RETRY_DELAYS_MS[this.retryAttempts];
    this.retryAttempts++;
    this.zone.runOutsideAngular(() => {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        if (this.currentUid() !== uid) {
          return;
        }
        this.zone.run(() => this.refreshQuietly());
      }, delay);
    });
  }

  /** Cancels a pending retry and starts the next failure stretch from zero. */
  private resetRetry(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryAttempts = 0;
    this.retryUid = null;
  }

  private release(request$: Observable<any>): void {
    if (this.inFlight$ === request$) {
      this.inFlight$ = null;
      this.inFlightUid = null;
    }
  }

  /** The signed-in credential's uid, or null when signed out. */
  currentUid(): string | null {
    const credential: any = this.authService.authStatusSub.getValue();
    return (credential && credential.uid) || null;
  }

  /**
   * The verdict in the store right now. NgRx's state is a BehaviorSubject
   * whose value is replaced before any subscriber hears of the change, so
   * take(1) answers synchronously — and from inside a store-derived emission
   * it already sees the payload that caused it. Public: the lobby page reads
   * the same thing when its visibility stream flips.
   */
  storedVerdict(): IAccountVirtualsVerdict | null {
    let latest: IAccountVirtualsVerdict | null = null;
    this.store
      .select(selectVirtualsVerdict)
      .pipe(take(1))
      .subscribe((verdict: IAccountVirtualsVerdict) => (latest = verdict));
    return latest;
  }
}
